import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabaseClient';
import type { CustomerWithStats } from '@/types';

export interface CustomerAppointmentDetail {
  id: string;
  appointment_date: string;
  start_time: string;
  status: string;
  payment_status: string;
  service_name: string | null;
  amount_paid: number;
}

export interface CustomerDetail extends CustomerWithStats {
  appointments: CustomerAppointmentDetail[];
}

export function useCustomers() {
  const [customers, setCustomers] = useState<CustomerWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch all customers
      const { data: custData, error: custErr } = await supabase
        .from('customers')
        .select('id, name, email, phone, created_at, updated_at')
        .order('created_at', { ascending: false });

      if (custErr) throw custErr;
      if (!custData || custData.length === 0) {
        setCustomers([]);
        return;
      }

      // Fetch appointments with payments for stats
      const customerIds = custData.map((c) => c.id as string);

      const { data: apptData, error: apptErr } = await supabase
        .from('appointments')
        .select('id, customer_id, appointment_date, status, payment_status')
        .in('customer_id', customerIds);

      if (apptErr) throw apptErr;

      const appointments = apptData ?? [];

      // Fetch payments for completed appointments
      const apptIds = appointments.map((a) => a.id as string);
      let paymentsMap = new Map<string, number>();

      if (apptIds.length > 0) {
        const { data: payData } = await supabase
          .from('payments')
          .select('appointment_id, amount, status')
          .in('appointment_id', apptIds)
          .eq('status', 'paid');

        for (const p of payData ?? []) {
          const prev = paymentsMap.get(p.appointment_id as string) ?? 0;
          paymentsMap.set(p.appointment_id as string, prev + (p.amount as number));
        }
      }

      // Build stats per customer
      const statsMap = new Map<string, { total: number; spent: number; lastVisit: string | null }>();
      for (const appt of appointments) {
        const cid = appt.customer_id as string;
        const prev = statsMap.get(cid) ?? { total: 0, spent: 0, lastVisit: null };
        const paid = paymentsMap.get(appt.id as string) ?? 0;
        const date = appt.appointment_date as string;
        statsMap.set(cid, {
          total: prev.total + 1,
          spent: prev.spent + paid,
          lastVisit:
            !prev.lastVisit || date > prev.lastVisit ? date : prev.lastVisit,
        });
      }

      const result: CustomerWithStats[] = custData.map((c) => {
        const stats = statsMap.get(c.id as string) ?? { total: 0, spent: 0, lastVisit: null };
        return {
          id: c.id as string,
          name: c.name as string,
          email: c.email as string,
          phone: (c.phone as string | null) ?? null,
          created_at: c.created_at as string,
          updated_at: c.updated_at as string,
          total_appointments: stats.total,
          total_spent: stats.spent,
          last_visit: stats.lastVisit,
        };
      });

      setCustomers(result);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Error al cargar clientes';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const filteredCustomers = customers.filter((c) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      c.name.toLowerCase().includes(q) ||
      c.email.toLowerCase().includes(q) ||
      (c.phone ?? '').toLowerCase().includes(q)
    );
  });

  return {
    customers: filteredCustomers,
    allCustomers: customers,
    loading,
    error,
    search,
    setSearch,
    refetch: fetchCustomers,
  };
}

export async function fetchCustomerDetail(customerId: string): Promise<CustomerDetail | null> {
  try {
    const { data: cust, error: custErr } = await supabase
      .from('customers')
      .select('id, name, email, phone, created_at, updated_at')
      .eq('id', customerId)
      .maybeSingle();

    if (custErr || !cust) return null;

    const { data: apptData, error: apptErr } = await supabase
      .from('appointments')
      .select('id, appointment_date, start_time, status, payment_status, services(name)')
      .eq('customer_id', customerId)
      .order('appointment_date', { ascending: false });

    if (apptErr) return null;

    const appointments = apptData ?? [];
    const apptIds = appointments.map((a) => a.id as string);

    let paymentsMap = new Map<string, number>();
    if (apptIds.length > 0) {
      const { data: payData } = await supabase
        .from('payments')
        .select('appointment_id, amount, status')
        .in('appointment_id', apptIds)
        .eq('status', 'paid');

      for (const p of payData ?? []) {
        const prev = paymentsMap.get(p.appointment_id as string) ?? 0;
        paymentsMap.set(p.appointment_id as string, prev + (p.amount as number));
      }
    }

    let totalSpent = 0;
    let lastVisit: string | null = null;

    const apptDetails: CustomerAppointmentDetail[] = appointments.map((a) => {
      const paid = paymentsMap.get(a.id as string) ?? 0;
      totalSpent += paid;
      const date = a.appointment_date as string;
      if (!lastVisit || date > lastVisit) lastVisit = date;
      return {
        id: a.id as string,
        appointment_date: date,
        start_time: a.start_time as string,
        status: a.status as string,
        payment_status: a.payment_status as string,
        service_name: (a.services as { name: string } | null)?.name ?? null,
        amount_paid: paid,
      };
    });

    return {
      id: cust.id as string,
      name: cust.name as string,
      email: cust.email as string,
      phone: (cust.phone as string | null) ?? null,
      created_at: cust.created_at as string,
      updated_at: cust.updated_at as string,
      total_appointments: appointments.length,
      total_spent: totalSpent,
      last_visit: lastVisit,
      appointments: apptDetails,
    };
  } catch {
    return null;
  }
}
